import { CheckCircle2, FileEdit, Loader2, Trash2 } from "lucide-react";
import MathText from "@/components/MathText";

/**
 * Review screen shown once Gemini returns drafts. Handles per-draft edit,
 * bulk publish, publish-as-exam, and discard. Parent owns the state and
 * exposes callbacks for each action.
 */
export default function DraftReview({
  meta, drafts, selected, onToggleSelect,
  edits, onEditText, onEditCorrect, onEditOption,
  saving, examBusy, examOpen, onToggleExamPanel,
  examForm, onExamFormChange,
  onPublish, onPublishAsExam, onDiscardAll,
  selectedCount,
}) {
  const getEdit = (d) => edits[d.id] || {
    text: d.text,
    options: [...(d.options || ["", "", "", ""])],
    correct_index: d.correct_index ?? 0,
    explanation: d.explanation || "",
  };

  return (
    <div className="space-y-5" data-testid="ai-textbook-review">
      <div className="qs-card p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600">Review drafts</p>
          <h3 className="font-display text-xl font-black text-slate-900 mt-1">
            {meta?.generated} question{meta?.generated === 1 ? "" : "s"} ready
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Source: {meta?.source_kind} · Model: {meta?.model} · {selectedCount} selected
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onDiscardAll}
            disabled={saving || examBusy}
            className="btn-ghost text-sm text-rose-600 disabled:opacity-50"
            data-testid="ai-textbook-discard-all"
          >
            <Trash2 className="w-4 h-4" /> Discard all
          </button>
          <button
            onClick={onToggleExamPanel}
            disabled={saving || examBusy || selectedCount === 0}
            className="btn-ghost text-sm border border-indigo-200 text-indigo-700 disabled:opacity-50"
            data-testid="ai-textbook-open-exam"
          >
            <FileEdit className="w-4 h-4" /> Send to new exam
          </button>
          <button
            onClick={onPublish}
            disabled={saving || examBusy || selectedCount === 0}
            className="btn-primary text-sm disabled:opacity-60"
            data-testid="ai-textbook-publish"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {saving ? "Publishing…" : `Publish ${selectedCount || ""} to Question Bank`}
          </button>
        </div>
      </div>

      {examOpen && (
        <div className="qs-card p-5 border-2 border-indigo-100" data-testid="ai-textbook-exam-panel">
          <div className="flex items-center gap-2 mb-3">
            <FileEdit className="w-4 h-4 text-indigo-600" />
            <h4 className="font-display text-base font-bold text-slate-900">Send {selectedCount} question{selectedCount === 1 ? "" : "s"} to a new exam</h4>
          </div>
          <p className="text-xs text-slate-500 mb-4">The selected questions will be published to your Question Bank and used to seed a new <b>draft exam</b>. You'll be redirected to the exam editor to review and publish.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-3">
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Exam title</label>
              <input
                value={examForm.title}
                onChange={(e) => onExamFormChange({ ...examForm, title: e.target.value })}
                className="w-full mt-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                data-testid="ai-textbook-exam-title"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Duration (min)</label>
              <input
                type="number" min={1} max={600}
                value={examForm.duration_min}
                onChange={(e) => onExamFormChange({ ...examForm, duration_min: e.target.value })}
                className="w-full mt-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                data-testid="ai-textbook-exam-duration"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Passing score (%)</label>
              <input
                type="number" min={0} max={100}
                value={examForm.passing_score}
                onChange={(e) => onExamFormChange({ ...examForm, passing_score: e.target.value })}
                className="w-full mt-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                data-testid="ai-textbook-exam-passing"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={onPublishAsExam}
                disabled={examBusy}
                className="btn-primary text-sm w-full justify-center disabled:opacity-60"
                data-testid="ai-textbook-exam-submit"
              >
                {examBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileEdit className="w-4 h-4" />}
                {examBusy ? "Building exam…" : "Create draft exam"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {drafts.map((d, idx) => {
          const e = getEdit(d);
          const isPicked = !!selected[d.id];
          return (
            <div
              key={d.id}
              className={`qs-card p-6 border-2 transition ${isPicked ? "border-indigo-200" : "border-transparent opacity-70"}`}
              data-testid={`ai-draft-${idx}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isPicked}
                    onChange={() => onToggleSelect(d.id)}
                    className="w-4 h-4"
                    data-testid={`ai-draft-select-${idx}`}
                  />
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-indigo-50 text-indigo-700">
                    Q{idx + 1}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">
                    {d.difficulty}
                  </span>
                  {(d.tags || []).slice(0, 3).map((t) => (
                    <span key={`${d.id}-tag-${t}`} className="text-[10px] font-semibold px-2 py-1 rounded-full bg-amber-50 text-amber-700">
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Question</label>
                <textarea
                  value={e.text}
                  onChange={(ev) => onEditText(d.id, ev.target.value)}
                  rows={3}
                  className="w-full mt-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none font-mono text-sm"
                  data-testid={`ai-draft-text-${idx}`}
                />
                <div className="mt-2 p-3 bg-white rounded-xl border border-slate-100 text-sm leading-relaxed text-slate-800">
                  <MathText>{e.text}</MathText>
                </div>
              </div>

              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {e.options.map((opt, i) => (
                  <div
                    key={`${d.id}-opt-${i}`}
                    className={`rounded-xl border p-3 ${i === e.correct_index ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <button
                        type="button"
                        onClick={() => onEditCorrect(d.id, i)}
                        className={`w-6 h-6 grid place-items-center rounded-full border text-[11px] font-bold transition ${
                          i === e.correct_index ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-300 text-slate-500 hover:border-emerald-400"
                        }`}
                        title="Mark as correct"
                        data-testid={`ai-draft-correct-${idx}-${i}`}
                      >
                        {String.fromCharCode(65 + i)}
                      </button>
                      <input
                        value={opt}
                        onChange={(ev) => onEditOption(d.id, i, ev.target.value)}
                        className="flex-1 bg-transparent border-0 outline-none text-sm font-mono"
                        data-testid={`ai-draft-option-${idx}-${i}`}
                      />
                    </div>
                    <div className="text-sm text-slate-700 pl-8">
                      <MathText>{opt}</MathText>
                    </div>
                  </div>
                ))}
              </div>

              {d.explanation && (
                <details className="mt-4 group">
                  <summary className="cursor-pointer text-xs font-semibold text-indigo-600 group-open:text-indigo-800">
                    Step-by-step derivation
                  </summary>
                  <div className="mt-2 p-3 rounded-xl bg-slate-50 border border-slate-100 text-sm leading-relaxed text-slate-700">
                    <MathText>{d.explanation}</MathText>
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
